import React, {useMemo} from 'react';
import {Table, Button, Dropdown, Input, Grid} from 'semantic-ui-react';
import {useTable, usePagination, useFilters} from 'react-table';
import {invoicesPageSizes} from '../../../common/constants/lists';
import {Filter, DefaultColumnFilter} from './TableFilters';

const columnsTable = [
  {
    'Header': 'MODELO',
    'accessor': 'model',
  },
  {
    'Header': 'NOMBRE COMERCIAL',
    'accessor': 'commercialName',
  },
  {
    'Header': 'SFX',
    'accessor': 'sfx',
  },
  {
    'Header': 'KATASHIKI',
    'accessor': 'katashiki',
  },
  {
    'Header': 'AÑO DE FABRICACIÓN',
    'accessor': 'svinFab',
  },
  {
    'Header': 'CHASIS',
    'accessor': 'chassis',
  },
  {
    'Header': 'MOTOR',
    'accessor': 'motor',
  },
  {
    'Header': 'COLOR',
    'accessor': 'color',
  },
  {
    'Header': 'CÉDULA',
    'accessor': 'ci',
  },
  {
    'Header': 'RESERVADO POR',
    'accessor': 'reserved',
  },
  {
    'Header': 'NOMBRE DEL CLIENTE',
    'accessor': 'nbrClient',
  },
  {
    'Header': 'FECHA DE VENTA DEL VEHÍCULO',
    'accessor': 'salesDateVehicle',
  },
  {
    'Header': 'FECHA FACTURACIÓN',
    'accessor': 'invoiceDate',
  },
  {
    'Header': 'FECHA PRIORIZACION',
    'accessor': 'prioritizationDate',
  },
  {
    'Header': 'SERIE',
    'accessor': 'serie',
  },
  {
    'Header': 'NO FACTU',
    'accessor': 'numInvoice',
  },
  {
    'Header': 'NO FISICO',
    'accessor': 'numPhysical',
  },
  {
    'Header': 'ORDEN',
    'accessor': 'order',
  },
  {
    'Header': 'COMBO',
    'accessor': 'combo',
  },
  {
    'Header': 'PRECIO',
    'accessor': 'price',
    'Cell': ({row}) =>
      (row.original.price ? `$ ${row.original.price.toFixed(2)}` : '--'),
  },
  {
    'Header': 'COSTO',
    'accessor': 'cost',
    'Cell': ({row}) =>
      (row.original.cost ? `$ ${row.original.cost.toFixed(2)}` : '--'),
  },
  {
    'Header': 'USUARIO',
    'accessor': 'user',
  },
];

const TableBody = ({page, prepareRow}) => {
  if (!page.length) {
    return (
      <Table.Row>
        <Table.Cell colSpan={12} textAlign='center'>
          No hay datos disponibles
        </Table.Cell>
      </Table.Row>
    );
  }

  return page.map((row, i) => {
    prepareRow(row);
    return (
      <Table.Row key={i} {...row.getRowProps()}>
        {row.cells.map((cell) => {
          return (
            <Table.Cell textAlign='center' {...cell.getCellProps()}>
              {cell.render('Cell')}
            </Table.Cell>
          );
        })}
      </Table.Row>
    );
  });
};

const TablePaginator = ({
  page,
  canPreviousPage,
  pageIndex,
  pageOptions,
  pageSize,
  canNextPage,
  gotoPage,
  nextPage,
  previousPage,
  setPageSize,
}) => {
  if (page.length) {
    return (
      <Grid className='pagination'>
        <Grid.Row>
          <Grid.Column width={3} textAlign='left'>
            <Button
              className='navigation-button'
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              content='Anterior'
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <span>páginas </span>
            <span>
              <Input
                className='pages-input'
                type='number'
                value={pageIndex + 1}
                min='1'
                max={pageOptions.length}
                onChange={(e, {value}) => {
                  const page = value ? Number(value) - 1 : 0;
                  if (page < 1 || page > pageOptions.length) {
                    e.target.value = 1;
                  }
                  gotoPage(page);
                }}
              />
            </span>{' '}
            <span>de {pageOptions.length}</span>
          </Grid.Column>
          <Grid.Column width={5}>
            <Dropdown
              className='page-sizes-selection'
              selection
              upward
              compact
              value={pageSize}
              options={invoicesPageSizes}
              onChange={(e, {value}) => {
                setPageSize(Number(value));
              }}
            />
            <span> filas</span>
          </Grid.Column>
          <Grid.Column width={3} textAlign='right'>
            <Button
              className='navigation-button'
              onClick={() => nextPage()}
              disabled={!canNextPage}
              content='Siguiente'
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
  return null;
};

const ReportCombosTable = ({dataTable}) => {
  const data = useMemo(() => dataTable, []);
  const columns = useMemo(() => columnsTable, []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    'state': {pageIndex, pageSize},
  } = useTable(
    {
      columns,
      data,
      'defaultColumn': {'Filter': DefaultColumnFilter},
      'initialState': {'pageIndex': 0, 'pageSize': 10},
    },
    useFilters,
    usePagination
  );

  return (
    <>
      <div className='table-container'>
        <Table className='interactive' compact striped {...getTableProps()}>
          <Table.Header>
            {headerGroups.map((headerGroup) => (
              <Table.Row key='headers' {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, index) => (
                  <Table.HeaderCell
                    key={`title-${index}`}
                    textAlign='center'
                    {...column.getHeaderProps()}
                  >
                    {column.render('Header')}
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            ))}
            {headerGroups.map((headerGroup) => (
              <Table.Row key='filters' id='filters'>
                {headerGroup.headers.map((column, index) => (
                  <Table.HeaderCell
                    key={`filter-${index}`}
                    className={
                      index === 3 || index === 6 ? 'dropdown-header' : ''
                    }
                    textAlign='center'
                  >
                    <Filter column={column} />
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            ))}
          </Table.Header>
          <Table.Body {...getTableBodyProps()}>
            <TableBody page={page} prepareRow={prepareRow} />
          </Table.Body>
        </Table>
      </div>
      <TablePaginator
        page={page}
        canPreviousPage={canPreviousPage}
        pageIndex={pageIndex}
        pageOptions={pageOptions}
        pageSize={pageSize}
        canNextPage={canNextPage}
        gotoPage={gotoPage}
        nextPage={nextPage}
        previousPage={previousPage}
        setPageSize={setPageSize}
      />
    </>
  );
};

export default ReportCombosTable;
